export interface IProductImage {
  id: string | null;
  filename: string | null;
  filename_2: string | null;
  filename_3: string | null;
  filename_4: string | null;
  filename_5: string | null;
  is_cover: boolean;
}

export type ProductImageSizeType =
  (typeof ProductImage.Sizes)[keyof typeof ProductImage.Sizes];

export default class ProductImage implements IProductImage {
  public static readonly Sizes = {
    Original: 1,
    Small: 2,
    Medium: 3,
    Large: 4,
    ExtraSmall: 5,
  } as const;

  public id: string | null = null;
  public is_cover: boolean = false;
  public filename: string | null = null;
  public filename_2: string | null = null;
  public filename_3: string | null = null;
  public filename_4: string | null = null;
  public filename_5: string | null = null;

  public constructor({
    id,
    filename,
    filename_2,
    filename_3,
    filename_4,
    filename_5,
    is_cover,
  }: IProductImage) {
    this.id = id;
    this.is_cover = is_cover;
    this.filename = filename;
    this.filename_2 = filename_2;
    this.filename_3 = filename_3;
    this.filename_4 = filename_4;
    this.filename_5 = filename_5;
  }

  public static fromArray(items: IProductImage[]): ProductImage[] {
    return items.map((item: IProductImage) => new ProductImage(item));
  }

  public static getFieldOfSize(size: ProductImageSizeType): string | null {
    if (size == ProductImage.Sizes.Original) {
      return "filename";
    }
    return `filename_${size}`;
  }

  public static getAllSizeFields() {
    const sizes = Object.values(ProductImage.Sizes);
    const sizeFields = sizes.map((size) => ProductImage.getFieldOfSize(size));
    return sizeFields;
  }

  public getImageOfSize(size: ProductImageSizeType): string | undefined {
    const field = ProductImage.getFieldOfSize(size);
    for (const key in this) {
      if (key == field) {
        return this[key] as string;
      }
    }
    return undefined;
  }

  public getThumbImage(): string | undefined {
    return (
      this.getImageOfSize(ProductImage.Sizes.ExtraSmall) ||
      this.getImageOfSize(ProductImage.Sizes.Small) ||
      this.getImageOfSize(ProductImage.Sizes.Medium) ||
      this.getImageOfSize(ProductImage.Sizes.Original)
    );
  }

  public getOriginalImage(): string | undefined {
    return this.getImageOfSize(ProductImage.Sizes.Original);
  }

  public getMediumImage(): string | undefined {
    return (
      this.getImageOfSize(ProductImage.Sizes.Medium) ||
      this.getImageOfSize(ProductImage.Sizes.Large) ||
      this.getImageOfSize(ProductImage.Sizes.Original)
    );
  }

  public getLargeImage(): string | undefined {
    return (
      this.getImageOfSize(ProductImage.Sizes.Large) ||
      this.getImageOfSize(ProductImage.Sizes.Original)
    );
  }

  public getBestImageOfSize(
    size: ProductImageSizeType = ProductImage.Sizes.Medium,
  ): string | undefined {
    if (size == ProductImage.Sizes.ExtraSmall) {
      return this.getThumbImage();
    }
    if (size == ProductImage.Sizes.Small) {
      return this.getThumbImage();
    } else if (size == ProductImage.Sizes.Medium) {
      return this.getMediumImage();
    } else if (size == ProductImage.Sizes.Large) {
      return this.getLargeImage();
    }
    return this.getOriginalImage();
  }
}
